import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy, ShieldCheck, Archive, GitCompare } from "lucide-react";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, EmptyState, DataTable, StatusPill } from "@/components/common";
import { useApp } from "@/lib/store";
import { fmtDate } from "@/lib/utils";

interface AdminPack {
  id: string; name: string; semver: string; kind: string; tier: string;
  universal: boolean; status: string; created_at: string;
}

interface DiffResult {
  added_triples: number; removed_triples: number; unchanged_triples: number;
  added_terms: Record<string, string[]>; removed_terms: Record<string, string[]>;
  parse_error: string | null;
}

/** Phase 7.3 — marketplace template (Pack) lifecycle administration.
 *  BE enforces staff-only access (`IsStaffUser`); this client-side gate is
 *  a UX convenience, not the security boundary. */
export function AdminTemplates() {
  const user = useApp((s) => s.user);
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser);
  const qc = useQueryClient();

  const [name, setName] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatusFilter] = useState("");
  const [cloningId, setCloningId] = useState<string | null>(null);
  const [cloneSemver, setCloneSemver] = useState("");

  const [compareName, setCompareName] = useState("");
  const [fromVer, setFromVer] = useState("");
  const [toVer, setToVer] = useState("");

  const qp = new URLSearchParams();
  if (name) qp.set("name", name);
  if (kind) qp.set("kind", kind);
  if (status) qp.set("status", status);

  const packs = useQuery<{ results: AdminPack[] }>({
    queryKey: ["admin-templates", name, kind, status],
    queryFn: () => rawFetch(`/studio/admin/templates/?${qp.toString()}`, undefined, "json", false),
    enabled: isAdmin,
  });

  const clone = useMutation({
    mutationFn: ({ id, semver }: { id: string; semver: string }) =>
      rawFetch(`/studio/admin/templates/${id}/clone/`,
        { method: "POST", body: JSON.stringify({ semver }) }, "json", false),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-templates"] });
      setCloningId(null);
      setCloneSemver("");
    },
  });
  const publish = useMutation({
    mutationFn: (id: string) =>
      rawFetch(`/studio/admin/templates/${id}/publish/`, { method: "POST" }, "json", false),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-templates"] }),
  });
  const retire = useMutation({
    mutationFn: (id: string) =>
      rawFetch(`/studio/admin/templates/${id}/retire/`, { method: "POST" }, "json", false),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-templates"] }),
  });

  const compare = useMutation({
    mutationFn: () => rawFetch<DiffResult>(
      `/studio/admin/templates/compare/?name=${encodeURIComponent(compareName)}` +
      `&from=${encodeURIComponent(fromVer)}&to=${encodeURIComponent(toVer)}`,
      undefined, "json", false,
    ),
  });

  if (!isAdmin) {
    return (
      <>
        <PageHeader title="Admin: Templates" subtitle="Marketplace template lifecycle." />
        <EmptyState title="Staff access required" hint="This page is restricted to staff accounts." />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Admin: Templates"
                  subtitle="Marketplace template (Pack) lifecycle — draft, publish, retire, clone and compare versions." />

      <div className="card mb-4">
        <div className="mb-2 text-xs uppercase text-muted">Filters</div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <input className="input" placeholder="name" value={name} onChange={(e) => setName(e.target.value)} />
          <input className="input" placeholder="kind" value={kind} onChange={(e) => setKind(e.target.value)} />
          <select className="input" value={status} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="PUBLISHED">Published</option>
            <option value="RETIRED">Retired</option>
          </select>
        </div>
      </div>

      {packs.isError && <ErrorBox error={packs.error} />}
      <DataTable
        rows={packs.data?.results ?? []}
        empty="No templates match these filters."
        cols={[
          { header: "Name",    cell: (p) => p.name },
          { header: "Version", cell: (p) => <code className="font-mono text-xs">{p.semver}</code> },
          { header: "Kind",    cell: (p) => p.kind },
          { header: "Status",  cell: (p) => <StatusPill status={p.status} /> },
          { header: "Created", cell: (p) => fmtDate(p.created_at) },
          { header: "", width: "1%", cell: (p) => (
              <div className="flex flex-wrap items-center gap-1">
                {cloningId === p.id ? (
                  <>
                    <input className="input w-24 text-xs" placeholder="new semver"
                           value={cloneSemver} onChange={(e) => setCloneSemver(e.target.value)} />
                    <button className="btn-primary" disabled={!cloneSemver || clone.isPending}
                            onClick={() => clone.mutate({ id: p.id, semver: cloneSemver })}>
                      Confirm
                    </button>
                    <button className="btn" onClick={() => { setCloningId(null); setCloneSemver(""); }}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <button className="btn" title="Clone as draft"
                          onClick={() => { setCloningId(p.id); setCloneSemver(""); }}>
                    <Copy size={14} />
                  </button>
                )}
                {p.status !== "PUBLISHED" && (
                  <button className="btn" title="Publish" disabled={publish.isPending}
                          onClick={() => publish.mutate(p.id)}>
                    <ShieldCheck size={14} />
                  </button>
                )}
                {p.status !== "RETIRED" && (
                  <button className="btn" title="Retire" disabled={retire.isPending}
                          onClick={() => retire.mutate(p.id)}>
                    <Archive size={14} />
                  </button>
                )}
              </div>
            ),
          },
        ]}
      />
      {clone.isError && <div className="mt-2"><ErrorBox error={clone.error} /></div>}
      {publish.isError && <div className="mt-2"><ErrorBox error={publish.error} /></div>}
      {retire.isError && <div className="mt-2"><ErrorBox error={retire.error} /></div>}

      <h2 className="mb-2 mt-6 text-sm font-medium text-muted">Compare versions</h2>
      <div className="card mb-4">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[2fr_1fr_1fr_auto]">
          <input className="input" placeholder="template name"
                 value={compareName} onChange={(e) => setCompareName(e.target.value)} />
          <input className="input" placeholder="from (e.g. 1.0.0)"
                 value={fromVer} onChange={(e) => setFromVer(e.target.value)} />
          <input className="input" placeholder="to (e.g. 1.1.0)"
                 value={toVer} onChange={(e) => setToVer(e.target.value)} />
          <button className="btn-primary" disabled={!compareName || !fromVer || !toVer || compare.isPending}
                  onClick={() => compare.mutate()}>
            <GitCompare size={14} className="mr-1 inline" /> Compare
          </button>
        </div>
        {compare.isError && <div className="mt-2"><ErrorBox error={compare.error} /></div>}
        {compare.data && (
          <div className="mt-3 text-sm">
            {compare.data.parse_error ? (
              <div className="text-bad">Parse error: {compare.data.parse_error}</div>
            ) : (
              <>
                <div className="mb-2 flex gap-4 text-xs text-muted">
                  <span>Added triples: <strong className="text-fg">{compare.data.added_triples}</strong></span>
                  <span>Removed triples: <strong className="text-fg">{compare.data.removed_triples}</strong></span>
                  <span>Unchanged: <strong className="text-fg">{compare.data.unchanged_triples}</strong></span>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <div className="mb-1 text-xs uppercase text-muted">Added terms</div>
                    {Object.entries(compare.data.added_terms).map(([kindKey, terms]) => (
                      terms.length > 0 && (
                        <div key={kindKey} className="mb-1">
                          <span className="text-xs font-medium text-fg">{kindKey}:</span>{" "}
                          <span className="text-xs text-muted">{terms.join(", ")}</span>
                        </div>
                      )
                    ))}
                  </div>
                  <div>
                    <div className="mb-1 text-xs uppercase text-muted">Removed terms</div>
                    {Object.entries(compare.data.removed_terms).map(([kindKey, terms]) => (
                      terms.length > 0 && (
                        <div key={kindKey} className="mb-1">
                          <span className="text-xs font-medium text-fg">{kindKey}:</span>{" "}
                          <span className="text-xs text-muted">{terms.join(", ")}</span>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
